import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toPaise, lineAmount } from '@vns/core';
import { supabase } from './supabase';
import type { EntryKind } from '../components/EntryDrawers';

/**
 * Correcting and removing transactions.
 *
 * Admin-only, and enforced in the database: site staff have no UPDATE or DELETE
 * policy on any of these tables, so a submitted entry stays locked to the person
 * who sent it. What an admin changes here is authoritative — an edit does NOT
 * send the row back for approval, because the admin IS the approver.
 *
 * Deletion is a soft delete, and every total in the product filters
 * `deleted_at is null`, so removing a payment corrects the project balance
 * immediately rather than leaving a stale figure behind.
 */

/** The six transaction tables, keyed by the entry kind their form handles. */
export const ENTRY_TABLE: Record<EntryKind, string> = {
  payment: 'client_payments',
  purchase: 'material_purchases',
  usage: 'material_usage',
  labour: 'subcontractor_labour',
  expense: 'expenses',
  transfer: 'stock_transfers',
};

type LooseTable = {
  update: (v: Record<string, unknown>) => {
    eq: (c: string, v: string) => PromiseLike<{ error: { message: string } | null }>;
  };
};
const looseFrom = supabase.from.bind(supabase) as unknown as (t: string) => LooseTable;

/** Editing or removing a transaction moves totals on many screens at once. */
function useRefreshEverything(projectId: string) {
  const qc = useQueryClient();
  return () => {
    for (const key of ['project', 'projects', 'dashboard', 'client', 'materials',
                       'bank-accounts', 'stock-transfers', 'report']) {
      void qc.invalidateQueries({ queryKey: [key] });
    }
    void qc.invalidateQueries({ queryKey: ['project', projectId] });
  };
}

async function write(table: string, id: string, values: Record<string, unknown>) {
  const { error } = await looseFrom(table).update(values).eq('id', id);
  if (error) throw new Error(error.message);
}

/* ------------------------------- delete ------------------------------- */

export function useDeleteEntry(projectId: string) {
  const refresh = useRefreshEverything(projectId);
  return useMutation({
    mutationFn: async ({ kind, id }: { kind: EntryKind; id: string }) => {
      await write(ENTRY_TABLE[kind], id, { deleted_at: new Date().toISOString() });
    },
    onSuccess: refresh,
  });
}

export function useRestoreEntry(projectId: string) {
  const refresh = useRefreshEverything(projectId);
  return useMutation({
    mutationFn: async ({ kind, id }: { kind: EntryKind; id: string }) => {
      await write(ENTRY_TABLE[kind], id, { deleted_at: null });
    },
    onSuccess: refresh,
  });
}

/* ------------------------------- update ------------------------------- */

export interface EditValues {
  date: string;
  remarks?: string;
  // payment
  payment_type?: string;
  amountRupees?: string;
  mode?: string;
  reference_no?: string;
  // purchase / usage / transfer
  material_id?: string;
  supplier_id?: string;
  unit_id?: string;
  quantity?: string;
  rateRupees?: string;
  advanceRupees?: string;
  invoice_no?: string;
  activity?: string;
  to_project_id?: string;
  vehicle_no?: string;
  driver_name?: string;
  // labour
  subcontractor_id?: string;
  work_type_id?: string;
  worker_count?: string;
  ratePerWorkerRupees?: string;
  totalRupees?: string;
  // expense
  title?: string;
  category_id?: string;
  paid_to?: string;
  bill_no?: string;
}

const text = (v?: string) => v?.trim() || null;

export function useUpdateEntry(projectId: string) {
  const refresh = useRefreshEverything(projectId);

  return useMutation({
    mutationFn: async ({ kind, id, v }: { kind: EntryKind; id: string; v: EditValues }) => {
      switch (kind) {
        case 'payment':
          return write('client_payments', id, {
            paid_on: v.date,
            payment_type: v.payment_type,
            amount_paise: toPaise(v.amountRupees ?? '0'),
            // mode is deliberately absent: the form no longer collects it, and
            // omitting the column leaves whatever an older row already has —
            // sending null would wipe it on every edit.
            reference_no: text(v.reference_no),
            remarks: text(v.remarks),
          });

        case 'purchase': {
          const qty = Number(v.quantity);
          const rate = toPaise(v.rateRupees ?? '0');
          // Recompute rather than trusting a stored total — the whole point of
          // an edit is that quantity or rate changed.
          const amount = lineAmount(qty, rate);
          const advance = v.advanceRupees?.trim() ? toPaise(v.advanceRupees) : 0;
          if (advance > amount) throw new Error('Advance cannot be more than the total amount');
          return write('material_purchases', id, {
            purchased_on: v.date,
            material_id: v.material_id,
            supplier_id: v.supplier_id,
            unit_id: v.unit_id || null,
            quantity: qty,
            rate_paise: rate,
            amount_paise: amount,
            advance_paid_paise: advance,
            invoice_no: text(v.invoice_no),
            remarks: text(v.remarks),
          });
        }

        case 'usage':
          return write('material_usage', id, {
            used_on: v.date,
            material_id: v.material_id,
            unit_id: v.unit_id || null,
            quantity: Number(v.quantity),
            activity: text(v.activity),
            remarks: text(v.remarks),
          });

        case 'labour': {
          const count = Number(v.worker_count);
          const rate = v.ratePerWorkerRupees?.trim() ? toPaise(v.ratePerWorkerRupees) : null;
          const total = v.totalRupees?.trim()
            ? toPaise(v.totalRupees)
            : rate !== null ? lineAmount(count, rate) : 0;
          return write('subcontractor_labour', id, {
            worked_on: v.date,
            subcontractor_id: v.subcontractor_id,
            work_type_id: v.work_type_id,
            worker_count: count,
            rate_per_worker_paise: rate,
            total_amount_paise: total,
            remarks: text(v.remarks),
          });
        }

        case 'expense':
          return write('expenses', id, {
            spent_on: v.date,
            category_id: v.category_id || null,
            title: (v.title ?? '').trim(),
            amount_paise: toPaise(v.amountRupees ?? '0'),
            paid_to: text(v.paid_to),
            bill_no: text(v.bill_no),
            remarks: text(v.remarks),
          });

        case 'transfer': {
          if (v.to_project_id === projectId) {
            throw new Error('Choose a different site to transfer to');
          }
          const qty = Number(v.quantity);
          const ratePaise = v.rateRupees && v.rateRupees.trim() ? toPaise(v.rateRupees) : null;
          const amountPaise = ratePaise != null ? Math.round(qty * ratePaise) : null;

          return write('stock_transfers', id, {
            transferred_on: v.date,
            to_project_id: v.to_project_id,
            material_id: v.material_id,
            unit_id: v.unit_id || null,
            quantity: qty,
            rate_paise: ratePaise,
            amount_paise: amountPaise,
            vehicle_no: text(v.vehicle_no),
            driver_name: text(v.driver_name),
            remarks: text(v.remarks),
          });
        }
      }
    },
    onSuccess: refresh,
  });
}
